import { createI18n } from 'vue-i18n';
import axios from 'axios';
import Logger from '/@/modules/core/app/helpers/Logger';
import DocumentUIService from '/@/modules/core/app/services/DocumentUIService';
import Locale from '/@/modules/user/Enum/Locale';
import DeveloperFormatter from '/@/modules/core/app/services/DeveloperFormatter';
import DeveloperLocale from '/@/modules/user/Enum/DeveloperLocale';
import dayjs from 'dayjs';
import { LocalStorageKey } from '@/modules/core/app/constants/app.constants';
import LocalStorage from '@/modules/core/app/helpers/LocalStorage';

export class Lang {
  /**
   * @var {I18n}
   */
  i18n;

  /**
   * @var {string[]}
   */
  loadedLanguages;

  constructor() {
    this.loadedLanguages = [];
  }

  init() {
    this.i18n = createI18n({
      legacy: true, // Use Legacy API mode for Vue 2 compatibility
      locale: Locale.ENGLISH.key, // Set default locale
      fallbackLocale: Locale.ENGLISH.key,
      silentFallbackWarn: true,
      silentTranslationWarn: true,
      formatFallbackMessages: true,
      messages: {} // Initialize with empty messages object
    });

    // In legacy mode, the i18n.global property contains the VueI18n instance
    // Bind translation methods for convenience
    this.t = this.i18n.global.t.bind(this.i18n.global);
    // In Vue I18n v9+, tc is deprecated - use t() for pluralization instead
    this.tc = this.i18n.global.tc ? this.i18n.global.tc.bind(this.i18n.global) : this.t;
  }

  setDeveloperMode() {
    this.i18n.global.formatter = DeveloperFormatter;
  }

  /**
   * @param {string} lang
   * @param {JurisdictionLanguage[]|array} jurisdictionLanguages
   */
  setI18nLanguage(lang, jurisdictionLanguages = []) {
    try {
      lang = Locale.getValuesByProp('key').includes(lang) ? lang : Locale.ENGLISH.key;
      this.setJurisdictionLocale(lang, jurisdictionLanguages);
      axios.defaults.headers.common['Accept-Language'] = lang;
      DocumentUIService.setLanguage(lang);
    } catch (e) {
      Logger.error(e.message);
    }
  }

  /**
   * @param {string} lang
   * @return {Promise<{default: {}}|*>}
   */
  async importLang(lang = Locale.ENGLISH.key) {
    return import(`./../../../../locales/${lang}.json`);
  }

  /**
   * @param {string} lang
   * @return {Promise<void>}
   */
  async loadLanguage(lang) {
    lang = Locale.getValuesByProp('key').includes(lang) ? lang : Locale.ENGLISH.key;

    if (this.loadedLanguages.includes(lang)) {
      return;
    }
    const messages = await this.importLang(lang);
    this.i18n.global.setLocaleMessage(lang, this.cleanDictionary(messages['default']));
    this.loadedLanguages.push(lang);
  }

  /**
   * Gets the current fallback locale value.
   * @return {string}
   */
  getFallbackLocaleValue() {
    return this.i18n.global.fallbackLocale;
  }

  /**
   * @param {string} lang
   * @param {JurisdictionLanguage[]|array} jurisdictionLanguages
   * @param {boolean} canToggleDeveloperMode
   * @return {Promise<void>}
   */
  async resetI18n(lang, jurisdictionLanguages = [], canToggleDeveloperMode = false) {
    if (lang === DeveloperLocale.DEVELOPER.key) {
      if (canToggleDeveloperMode) {
        this.setDeveloperMode();
      }
      return;
    }

    this.setI18nLanguage(lang, jurisdictionLanguages);
    await this.loadLanguage(this.getLocaleValue());
    await this.loadLanguage(this.getFallbackLocaleValue());

    LocalStorage.setItem(LocalStorageKey.USER_LOCALE, this.getLocaleValue());

    dayjs.locale(this.getLocaleValue().split('-')[0]);
  }

  /**
   * since vue-i18n treats an empty string as a valid translation,
   * this is needed until https://github.com/kazupon/vue-i18n/issues/563 is implemented
   * @param {*} messages
   */
  cleanDictionary(messages) {
    Object.keys(messages).forEach((key) => {
      if (!messages[key]) {
        delete messages[key];
      }
    });
    return messages;
  }

  /**
   * Gets the current locale value.
   * @return {string}
   */
  getLocaleValue() {
    return this.i18n.global.locale;
  }

  /**
   * Sets the locale value.
   * @param {string} locale
   */
  setLocaleValue(locale) {
    this._setComposerValue('locale', locale);
    this.i18n.global.locale = locale;
  }

  /**
   * Sets the fallback locale value.
   * @param {string} locale
   */
  setFallbackLocaleValue(locale) {
    this._setComposerValue('fallbackLocale', locale);
    this.i18n.global.fallbackLocale = locale;
  }

  /**
   * Sets a value on the internal composer, handling deactivated reactive effects.
   *
   * In vue-i18n v11, locale values are stored in ComputedRefImpl objects.
   * When Vue components unmount during test cleanup, reactive effects get deactivated,
   * preventing normal value assignment from working. This directly sets the internal
   * _value property to bypass the deactivated reactive system.
   *
   * @param {string} prop - Property name ('locale' or 'fallbackLocale')
   * @param {string} value - Value to set
   * @private
   */
  _setComposerValue(prop, value) {
    const ref = this.i18n.global.__composer?.[prop];
    if (ref?.effect?.active === false) {
      ref._value = value;
    }
  }

  /**
   * @return {boolean}
   */
  isFrench() {
    return this.getLocaleValue().startsWith('fr-');
  }

  get locale() {
    const currentLocale = this.getLocaleValue();
    return Locale.getValuesByProp('key').includes(currentLocale)
      ? currentLocale
      : Locale.ENGLISH.key;
  }

  get languageLocale() {
    return this.isFrench() ? Locale.FRENCH.key : Locale.ENGLISH.key;
  }

  /**
   * @return {string}
   */
  getLocaleCurrency() {
    if (this.isFrench()) {
      return Locale.FRENCH.currency;
    }

    return Locale.ENGLISH.currency;
  }

  /**
   * @param {number} value
   * @returns {string}
   */
  number(value) {
    if (this.isFrench()) {
      return value.toString(10).replace('.', ',');
    }

    return value.toString(10);
  }

  /**
   * @param lang
   * @param {JurisdictionLanguage[]|array} jurisdictionLanguages
   */
  setJurisdictionLocale(lang, jurisdictionLanguages = []) {
    const languageKey = Locale.getByKey(lang).key.split('-')[0];

    this.setLocaleValue(lang);

    if (jurisdictionLanguages.map((elem) => elem.language).includes(languageKey)) {
      const jurisdictionLocale = jurisdictionLanguages.find(
        (elem) => elem.language === languageKey
      ).locale;
      const jurisdictionFallback = jurisdictionLanguages.find(
        (elem) => elem.language === languageKey
      ).fallbackLocale;

      this.setLocaleValue(jurisdictionLocale);
      this.setFallbackLocaleValue(jurisdictionFallback);
    }
  }
}

const lang = new Lang();
lang.init();

export default lang;
